/** Angualr imports */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, HostListener, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';

/** Core imports */
import { CreateUserStepsApiService, CreateUserStepsService, UsersService } from '@core';
import { CreateUserDTO } from '@core/dto';

/** Component imports */
import { Button, Modal, StepClickEvent, Stepper, StepperConfig } from '@ntv360/component-pantry';

/** Local imports */

import { UserRoles } from '@features/users/interfaces';

/** Steps Component */
import { ButtonComponent, DEFAULT_USER_AVATAR } from '@shared';
import { DraftsComponent } from './drafts/drafts.component';
import {
    CreateNewUserComponent,
    InputInformationComponent,
    PreviewDetailsComponent,
    SelectDealerComponent,
} from './steps';

/** Local services */
import { DraftApiService, FilestackUploadService } from '@core';
import { DEFAULT_STEPPER_CONFIG } from '@shared/constants';
import { citySelectionKeyToApiCityName } from '@shared/utils';
import { isEqual } from 'lodash';
import { first } from 'rxjs';

const stepsComponent = [
    InputInformationComponent,
    CreateNewUserComponent,
    SelectDealerComponent,
    PreviewDetailsComponent,
];

@Component({
    selector: 'app-create-user',
    standalone: true,
    imports: [Stepper, Button, ...stepsComponent, ButtonComponent, Modal, DraftsComponent],
    templateUrl: './create-user.component.html',
    styleUrl: './create-user.component.scss',
})
export class CreateUserComponent {
    /**
     * Private static step IDS
     */
    private static readonly INPUT_INFORMATION_STEP_ID = 'input-information';
    private static readonly SELECT_DEALER_STEP_ID = 'select-dealer';
    private static readonly PREVIEW_DETAILS_STEP_ID = 'preview-details';
    private static readonly MAX_PASSWORD_LENGTH = 72;
    private static readonly DEFAULT_PROFILE_IMAGE_PATH = '/default_profile.png';
    private static readonly DEFAULT_PROFILE_IMAGE_FILENAME = 'default_profile.png';

    /**
     * Inject create user service to allow communication from all other sub-pages
     */
    private createUserStepsService = inject(CreateUserStepsService);

    /**
     * Inject create user steps API service to preload cities/state data for the autocomplete.
     */
    private createUserStepsApiService = inject(CreateUserStepsApiService);

    /**
     * Inject draft api service. This service is responsible for saving each step data on the backend draft
     */
    private draftApiService = inject(DraftApiService);

    /**
     * Inject users service to perform the actual user creation
     */
    private usersService = inject(UsersService);

    /**
     * Inject router to navigate to users page after success
     */
    private router = inject(Router);

    /**
     * Inject filestack service to upload profile picture
     */
    private filestackService = inject(FilestackUploadService);

    /**
     * Component reference for the input-information component
     */
    protected inputInformationComponentRef = viewChild(InputInformationComponent);

    /**
     * Component reference for the select-dealer component
     */
    protected selectDealerComponentRef = viewChild(SelectDealerComponent);

    /** Steps sub-steps reference */
    protected stepsSubSteps = signal<Record<string, any[]>>({});

    /** Whether the user is currently updated */
    protected isUserUpdated = signal<boolean>(false);

    /** Last create-user API failure (proxy often hides details; we still show the best message we have). */
    protected submitError = signal<string | null>(null);

    /**
     * Flag to whether to show the drafts modal
     */
    protected showDraftsModal = signal(false);
    /**
     * Show confirmation modal when navigating back to Step 1 from Step 2+.
     */
    protected showBackToStepOneModal = signal(false);

    /**
     * Pending step index to navigate to after confirmation.
     */
    protected pendingStepNavigationIndex = signal<number | null>(null);

    /** Shows success confirmation modal after account creation. */
    public readonly showVerificationLinkModal = signal(false);

    /** Email displayed in success modal after account creation. */
    public readonly verificationEmail = signal<string>('');

    /**
     * Get all create user steps based on user type (for now dle sa trial paman)
     * @readonly
     */
    protected createUserSteps = computed(() => {
        return this.createUserStepsService.steps();
    });

    /**
     * Current step index
     * @readonly
     */
    protected currentStepIndex = this.createUserStepsService.currentStepIndex;

    /**
     * Last visited step index
     * @readonly
     */
    protected lastVisitedStepIndex = this.createUserStepsService.lastVisitedStepIndex;

    /**
     * Selected user type to be created
     * @readonly
     */
    protected selectedUserType = this.createUserStepsService.selectedUserType;

    /**
     * Confirmation checkbox state
     */
    protected isConfirmationChecked = this.createUserStepsService.isConfirmationChecked;

    /**
     * Current step label
     * @readonly
     */
    protected currentStepLabel = this.createUserStepsService.currentStepLabel;

    /**
     * Current step ID
     * @readonly
     */
    protected currentStepId = this.createUserStepsService.currentStepId;

    /**
     * Whether a draft api async operation is underway
     */
    protected isDraftApiLoading = this.draftApiService.isLoading;

    /**
     * Whether a local async operation is underway
     */
    protected isLoading = this.createUserStepsService.isLoading;

    /**
     * Hide for techmagic demo
     */
    private hideDraftButton = signal(true);

    /**
     * Template helper for conditional rendering in the header.
     */
    protected isHiddenForNow(): boolean {
        return this.hideDraftButton();
    }

    /**
     * Computes the stepper configuration while disabling clicks during draft save/load operations.
     * Also hides descriptions to maintain a cleaner stepper UI.
     */
    public stepperConfig = computed((): StepperConfig => ({
        ...DEFAULT_STEPPER_CONFIG,
        clickable: true,
        showDescriptions: false,
    }));

    /**
     * Constructor to set the number of substeps based on the current step
     */
    constructor() {
        // Register the sub-steps map once so the service knows sub-step counts
        // for ALL steps upfront (needed for flicker-free goPrevStep navigation)
        this.createUserStepsService.registerStepSubSteps(this.stepsSubSteps());

        /**
         * Preload the cities/state dataset as soon as the create-user flow opens so that
         * the `City` autocomplete in the input-information step has data ready on first view.
         */
        this.createUserStepsApiService
            .getCities({ limit: 50 })
            .pipe(first())
            .subscribe({
                next: () => {},
                error: (error) => {
                    console.error('Error preloading cities for create-user flow:', error);
                },
            });
    }

    /**
     * On previous button click
     */
    public goPrev(): void {
        const currentStepId = this.currentStepId();
        const currentStepIndex = this.currentStepIndex();

        // Check if the current step data is complete before going back
        const isComplete = this.createUserStepsService.isCurrentStepComplete();
        this.createUserStepsService.setStepError(currentStepId, !isComplete);

        // If Step 2 is about to go back to Step 1, require confirmation.
        if (currentStepIndex === 1) {
            this.pendingStepNavigationIndex.set(0);
            this.showBackToStepOneModal.set(true);
            return;
        }

        if (currentStepIndex > 0) {
            this.createUserStepsService.goPrevStep();
        }
    }

    /**
     * On next button click
     */
    public async goNext(): Promise<void> {
        const currentStepIndex = this.currentStepIndex(); // Zero-based indexing, 0 for create-new-user (select user to create) and so on
        const currentStepId = this.currentStepId(); // Eg. input-information | select-dealer

        if (currentStepIndex < this.createUserSteps().length - 1) {
            // If we're in the input-information step, validate form fields and proceed to the next step if valid
            if (currentStepId === CreateUserComponent.INPUT_INFORMATION_STEP_ID) {
                const inputInformationComponent = this.inputInformationComponentRef();
                if (inputInformationComponent) {
                    inputInformationComponent.validateInformationForm();
                    if (inputInformationComponent.form().valid) {
                        inputInformationComponent.saveInformation();
                    }
                }
            } else if (currentStepId === CreateUserComponent.SELECT_DEALER_STEP_ID) {
                const selectDealerComponent = this.selectDealerComponentRef();
                if (selectDealerComponent) {
                    const hasSelectedDealers = selectDealerComponent.validateSelectDealer();
                    if (hasSelectedDealers) {
                        selectDealerComponent.saveInformation();
                    }
                }
            }
        } else {
            await this.createUser();
        }
    }

    /**
     * Handles the step click event from the stepper component.
     *
     * @param step - The step click event containing the index of the clicked step
     *
     * @remarks
     * Navigation is only permitted to steps that have been previously visited.
     * This prevents the user from jumping ahead to incomplete steps while
     * still allowing free navigation to any already-visited step.
     *
     * @example
     * // User is on Step 3, lastVisitedStepIndex = 3
     * handleStepClick({ index: 1 }) // ✅ allowed - step 1 was previously visited
     * handleStepClick({ index: 4 }) // ❌ blocked - step 4 has not been visited yet
     */
    protected handleStepClick(step: StepClickEvent): void {
        const currentStepId = this.currentStepId();
        const currentStepIndex = this.currentStepIndex();

        // 1). Navigate to the specific step, ONLY if the last visited step index is greater than or equal to the step.index
        if (this.lastVisitedStepIndex() >= step.index) {
            // 2). Check if the current step data is complete before navigating to the selected new step
            const isComplete = this.createUserStepsService.isCurrentStepComplete();
            this.createUserStepsService.setStepError(currentStepId, !isComplete);
            // Require confirmation if user is in Step 2+ and wants to go back to Step 1.
            if (currentStepIndex > 0 && step.index === 0) {
                this.pendingStepNavigationIndex.set(step.index);
                this.showBackToStepOneModal.set(true);
                return;
            }

            this.createUserStepsService.goToStepIdx(step.index);
        }
    }

    /**
     * Closes the back-to-step-one confirmation modal.
     */
    protected cancelBackToStepOneNavigation(): void {
        this.pendingStepNavigationIndex.set(null);
        this.showBackToStepOneModal.set(false);
    }

    /**
     * Confirms navigation back to Step 1.
     */
    protected confirmBackToStepOneNavigation(): void {
        const pendingStepIndex = this.pendingStepNavigationIndex();
        if (pendingStepIndex !== null) {
            // Reset stepper visual state when explicitly returning to Step 1.
            this.createUserStepsService.setValidStep();
            this.createUserSteps()
                .map((step) => step.id)
                .forEach((stepId) => this.createUserStepsService.setStepError(stepId, false));
            this.createUserStepsService.goToStepIdx(pendingStepIndex);
        }
        this.pendingStepNavigationIndex.set(null);
        this.showBackToStepOneModal.set(false);
    }

    /**
     * A listener that would be invoked when the user closes the window or refreshes the page
     *
     * Why I did this?
     *
     * Since in host revamped, they make network request on every field change, let's say the user changes the city selection,
     * they would trigger a network request to save that to the db, but that's too much network request given let's say you have 20 fields
     * on a single step, that's 20 network request, so I decided to make a network request only when the user clicks next or back
     * or when the user closes the window or refreshes the page
     *
     * Why I used navigator.sendBeacon instead of fetch
     *
     * @remarks
     * GPT it up as to why
     */

    @HostListener('window:beforeunload', ['$event'])
    public onBeforeUnload(event: Event): void {
        const draft = this.createUserStepsService.getDraft(this.selectedUserType()?.id!);
        // Check if it atleast has one value in it and if we should save the draft
        if (Object.keys(draft).length > 0 && this.shouldSaveDraft()) {
            this.saveDraft();
        }
    }

    /** We also want to save the draft when the user moves to another tab or window or gets out of focus */
    @HostListener('document:visibilitychange')
    public onVisibilityChange(): void {
        if (document.visibilityState === 'hidden') {
            if (this.shouldSaveDraft()) {
                this.saveDraft();
                this.createUserStepsService.setLastSyncedDraft(
                    this.createUserStepsService.getDraft(this.selectedUserType()?.id!),
                );
            }
        }
    }

    /**
     * Save draft partial or complete data to the backend. See comment above for more details.
     *
     * @remarks
     * Persists the current draft data to the backend.
     * This method is strategically triggered on page unload or visibility changes
     * to batch multiple field updates into a single network request.
     *
     * @returns {void}
     */
    private saveDraft(): void {
        const draft = this.createUserStepsService.getDraft(this.selectedUserType()?.id!);

        /**
         * FUTURE API IMPLEMENTATION:
         * When your backend is ready, use fetch with 'keepalive' to ensure
         * the request survives page refresh or closure.
         *
         * @example
         * fetch('http://localhost:3000/api/draft', {
         *   method: 'POST',
         *   body: JSON.stringify(draft),
         *   headers: { 'Content-Type': 'application/json' },
         *   keepalive: true
         * });
         */

        // CURRENT TEMPORARY MOCK (Synchronous so it works during refresh)
        this.draftApiService.saveToLocaleStorage(draft);
    }

    /**
     * Whether we call the saveToDraft method when the user moves to another tab or window or gets out of focus
     *
     * @description
     * Determines if the current draft state differs from the last persisted version.
     * This prevents redundant API calls by verifying that the user has actually
     * modified the form fields since the last successful sync.
     *
     * @returns {boolean} True if the draft is "dirty" (has unsaved changes), false otherwise.
     */
    private shouldSaveDraft(): boolean {
        const draft = this.createUserStepsService.getDraft(this.selectedUserType()?.id!);
        const lastSyncedDraft = this.createUserStepsService.lastSyncedDraft();
        return !isEqual(draft, lastSyncedDraft);
    }

    /**
     * Transforms raw wizard draft data into a structured payload for user creation.
     * * This method aggregates data from multiple wizard steps (`input-information`
     * and `select-dealer`), performs snake_case conversion on the
     * role type, and conditionally maps extra fields if the user role is 'dealer'.
     *
     * @private
     * @returns {CreateUserDTO} The formatted Data Transfer Object ready for the API request.
     * * @example
     * // If role is 'subDealer', clientType becomes 'sub_dealer'
     * // If role is 'dealer', clientInformation is populated.
     */
    private preparePayload(): CreateUserDTO {
        const role = this.selectedUserType()?.id as Exclude<UserRoles, 'none'>;
        const draft = this.createUserStepsService.getDraft(role) as any;

        const info = draft['input-information'] ?? {};
        const dealers = draft['select-dealer']?.selectedDealers ?? [];

        const payload: CreateUserDTO = {
            clientType: role.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`) as any,
            userDetails: {
                username: info.emailAddress, // Fallback to emailAddress as username
                emailAddress: info.emailAddress,
                firstName: info.firstName,
                lastName: info.lastName,
                contactNumber: info.contactNumber || info.contactPersonPhone,
                statusId: 1, // Default status for new users
                password: info.password,
                passwordConfirmation: info.passwordConfirmation,
            },
        };

        // Populate clientInformation for dealers
        if (role === 'dealer') {
            payload.clientInformation = {
                businessName: info.businessName || '',
                region: info.region,
                state: info.state,
                city: citySelectionKeyToApiCityName(info.city),
                alias: info.dealerAlias,
                dateActivated: new Date().toISOString().split('T')[0],
                metadata: JSON.stringify({
                    contactPerson: info.contactPerson ?? '',
                }),
            };
        }

        // Map selected dealers to assignedDealers array (for sub-dealers, admins, etc.)
        if (dealers && dealers.length > 0) {
            payload.assignedDealers = dealers.map((d: any) => d.value);
        }

        return payload;
    }

    /**
     * Executes the end-to-end user creation process.
     * * This flow includes:
     * 1. Setting the global loading state.
     * 2. Checking for and uploading a profile picture via `FilestackService`.
     * 3. Preparing the `CreateUserDTO` payload from step drafts.
     * 4. Injecting uploaded image URLs into the payload metadata.
     * 5. Submitting the payload to the `UsersService`.
     * * On success, it resets the creation wizard state and navigates back to the user list.
     * On failure (either during upload or creation), it handles the error and stops the loader.
     * * @async
     * @private
     * @returns {Promise<void>} A promise that resolves when the upload phase is complete
     * and the creation request has been initiated.
     */
    private async createUser(): Promise<void> {
        this.submitError.set(null);
        if (!this.validatePasswordLength()) {
            return;
        }
        this.createUserStepsService.setIsLoading(true);

        try {
            const profileFile = this.createUserStepsService.profilePictureFile();

            let profilePictureUrls: { cdnUrl: string; s3Url: string } | null = null;

            if (profileFile) {
                profilePictureUrls = await this.filestackService.uploadFile(profileFile);
            } else {
                profilePictureUrls = {
                    cdnUrl: DEFAULT_USER_AVATAR,
                    s3Url: DEFAULT_USER_AVATAR,
                };
            }

            const payload = this.preparePayload();

            // Inject the uploaded CDN url into the userDetails structure.
            if (profilePictureUrls) {
                payload.userDetails.profilePicture = profilePictureUrls.cdnUrl;
                payload.userDetails.metadata = JSON.stringify(payload.userDetails.metadata || {});
            }

            this.usersService.createUser(payload).subscribe({
                next: () => {
                    this.createUserStepsService.setIsLoading(false);
                    this.submitError.set(null);
                    this.verificationEmail.set(payload.userDetails.emailAddress ?? '');
                    this.showVerificationLinkModal.set(true);
                },
                error: (err: unknown) => {
                    this.createUserStepsService.setIsLoading(false);
                    const message = this.getCreateUserErrorMessage(err);
                    this.submitError.set(message);
                    console.error('Error creating user:', err, message);
                },
            });
        } catch (error) {
            this.createUserStepsService.setIsLoading(false);
            const message = this.getCreateUserErrorMessage(error);
            this.submitError.set(message);
            console.error('Error during file upload:', error);
        }
    }

    /**
     * Enforces password length limits before submit.
     */
    private validatePasswordLength(): boolean {
        const role = this.selectedUserType()?.id as Exclude<UserRoles, 'none'> | undefined;
        if (!role) return false;

        const draft = this.createUserStepsService.getDraft(role) as Record<string, unknown>;
        const information = (draft['input-information'] ?? {}) as Record<string, unknown>;
        const password = String(information['password'] ?? '');
        const passwordConfirmation = String(information['passwordConfirmation'] ?? '');

        if (
            password.length > CreateUserComponent.MAX_PASSWORD_LENGTH ||
            passwordConfirmation.length > CreateUserComponent.MAX_PASSWORD_LENGTH
        ) {
            this.submitError.set(
                `Password must not exceed ${CreateUserComponent.MAX_PASSWORD_LENGTH} characters.`,
            );
            return false;
        }

        return true;
    }

    /**
     * Best-effort message for failed create (BFF proxy may only return generic 400 text).
     */
    private getCreateUserErrorMessage(err: unknown): string {
        if (err instanceof HttpErrorResponse) {
            const body = err.error;
            if (typeof body === 'string' && body.trim()) {
                return body.trim();
            }
            if (body && typeof body === 'object') {
                const b = body as Record<string, unknown>;
                if (typeof b['detail'] === 'string') return b['detail'];
                if (typeof b['message'] === 'string') return b['message'];
                if (typeof b['errorDescription'] === 'string') return b['errorDescription'];
                const errs = b['errors'];
                if (Array.isArray(errs) && errs.length > 0) {
                    const first = errs[0];
                    if (typeof first === 'string') return first;
                    if (first && typeof first === 'object' && 'message' in first) {
                        return String((first as { message?: string }).message ?? 'Request failed');
                    }
                }
            }
            if (err.message) return err.message;
        }
        if (
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as Error).message === 'string'
        ) {
            return (err as Error).message;
        }
        return 'Could not create the user. Check your details and try again.';
    }

    /**
     * @protected
     * @description Navigates back to the users list page
     * @returns void
     */
    protected backToUsers(): void {
        this.router.navigate(['/users']);
    }

    /**
     * @public
     * @description Closes success modal and navigates to users list.
     * @returns void
     */
    public closeVerificationLinkAndSuccessModal(): void {
        this.showVerificationLinkModal.set(false);
        this.createUserStepsService.clearDraft();
        this.createUserStepsService.setProfilePictureFile(null);

        this.router.navigate(['/users']);
    }
}
